import { useMemo, useState } from 'react';
import { useReduxState } from 'hooks';

import FilterButtonCheckboxItem from './FilterButtonCheckboxItem';
import FilterButtonRadioGroup from './FilterButtonRadioGroup';
import FilterButtonSingleItem from './FilterButtonSingleItem';
import FilterButtonSubMenu from './FilterButtonSubMenu';
import FilterHeader from './FilterHeader';
import { useFiltersContext, useFiltersDispatch } from './FilterContext';

import type { FilterItem } from '.';

interface Props extends FilterItem {
  filterId: string;
  parentPortalId: string;
  setRootIsOpen: (isOpen: boolean) => void;
}

const FilterButtonItems = ({
  allowMultiple = false,
  filterId,
  field,
  items,
  itemsAll,
  parentPortalId,
  selectAllOnClick,
  setRootIsOpen,
  title
}: Props) => {
  const { state } = useFiltersContext();

  const filterDispatch = useFiltersDispatch();

  const rootItemState = state?.[filterId]?.[field];

  const { messages, theme } = useReduxState();

  const [search, setSearch] = useState('');

  const updateRootItemState = (newState) => {
    if (newState) {
      filterDispatch({
        type: 'updateState',
        state: { ...state?.[filterId], [field]: newState },
        filterId
      });
    } else {
      const newState = { ...state?.[filterId] };
      delete newState[field];
      if (newState.presentInAll?.[field]) {
        newState.presentInAll = { ...newState.presentInAll };
        delete newState.presentInAll[field];
        if (Object.keys(newState.presentInAll).length === 0) {
          delete newState.presentInAll;
        }
      }
      filterDispatch({ type: 'updateState', state: newState, filterId });
    }
  };

  const handleCheckedAllItem = (
    checked: boolean,
    value: string,
    subItemParentValue?: string
  ) => {
    if (checked) {
      if (subItemParentValue) {
        const newState = { ...rootItemState };
        const existingKeys = Object.keys(newState);
        existingKeys.forEach((existingKey) => {
          if (existingKey.startsWith(`${subItemParentValue}.`)) {
            delete newState[existingKey];
          }
        });
        updateRootItemState({ ...newState, [value]: true });
      } else {
        updateRootItemState({ [value]: true });
      }
    } else {
      const newState = { ...rootItemState };
      delete newState[value];
      updateRootItemState(Object.keys(newState).length > 0 ? newState : null);
    }
  };

  const handleCheckedSubItem = (
    checked: boolean,
    value: string,
    allValue?: string
  ) => {
    if (checked) {
      const newState = { ...rootItemState };
      if (allValue) delete newState[allValue];
      updateRootItemState({ ...newState, [value]: true });
    } else {
      const newState = { ...rootItemState };
      delete newState[value];
      updateRootItemState(Object.keys(newState).length > 0 ? newState : null);
    }
  };

  const handleSelectedRadioSubItem = (value: string) => {
    if (rootItemState?.[value]) {
      updateRootItemState(null);
    } else {
      updateRootItemState({ [value]: true });
    }
  };

  const handleMenuToggle = (value: string) => {
    if (rootItemState?.[value]) {
      const newState = { ...rootItemState };
      delete newState[value];
      updateRootItemState(Object.keys(newState).length > 0 ? newState : null);
    } else {
      updateRootItemState({ ...rootItemState, [value]: true });
    }
  };

  const handleSubItemsClear = (value: string) => {
    const keysToRemove = Object.keys(rootItemState || {}).filter((key) =>
      key.startsWith(value)
    );

    const newState = { ...rootItemState };
    keysToRemove.forEach((key) => delete newState[key]);
    updateRootItemState(Object.keys(newState).length > 0 ? newState : null);
  };

  const visibleItems = useMemo(
    () =>
      items?.filter((item) =>
        search ? new RegExp(search, 'i').test(item.label) : true
      ) || [{ label: title, value: 'true' }],
    [items, search]
  );

  const ItemElement = allowMultiple
    ? FilterButtonCheckboxItem
    : FilterButtonSingleItem;

  return (
    <div>
      <FilterHeader
        field={field}
        filterId={filterId}
        initialSwitchValue={state?.[filterId]?.presentInAll?.[field] || false}
        searchActive={Boolean(items?.length)}
        switchActive={
          allowMultiple &&
          /accountType|achievementId|ambitionId|calendarEventId|competenceId|courseId|groupId|pathId|supervisorId/.test(
            field
          )
        }
        onSearch={(searchValue) => setSearch(searchValue)}
      />

      <div className="items">
        {!visibleItems?.length ? (
          <div className="empty">{messages.commonNoOptions}</div>
        ) : null}

        {itemsAll ? (
          <div className="item">
            <ItemElement
              checked={rootItemState?.[itemsAll.value]}
              label={itemsAll.label}
              onCheckedChange={(checked) =>
                handleCheckedAllItem(checked, itemsAll.value)
              }
              withCheckbox={true}
            />
          </div>
        ) : null}

        {visibleItems?.some((item) => item.subItems?.length) ? (
          visibleItems.map((item) => {
            const itemsActiveCount = Object.keys(rootItemState || {}).filter(
              (key) => {
                return (
                  key.split('.')?.[0] === item.value &&
                  (!item.subItemsAll ||
                    key.split('.')?.[1] !== item.subItemsAll.value)
                );
              }
            ).length;

            return item.subItems?.length ? (
              <div className="item" key={item.value}>
                <FilterButtonSubMenu
                  checked={
                    item.subItemsAll &&
                    rootItemState?.[`${item.value}.${item.subItemsAll.value}`]
                      ? true
                      : itemsActiveCount > 0
                      ? 'indeterminate'
                      : false
                  }
                  filterId={filterId}
                  icon={null}
                  itemsActiveCount={
                    allowMultiple ? itemsActiveCount : undefined
                  }
                  parentPortalId={parentPortalId}
                  portalId={`${field}-children`}
                  onClear={() => handleSubItemsClear(item.value)}
                  onSubMenuClick={
                    selectAllOnClick && item.subItemsAll && !itemsActiveCount
                      ? () =>
                          handleMenuToggle(
                            `${item.value}.${item.subItemsAll.value}`
                          )
                      : undefined
                  }
                  setRootIsOpen={setRootIsOpen}
                  title={item.label}
                  titleSubtitle={item.labelSubtitle}
                  withCheckbox={true}
                >
                  {!item.subItems?.length ? (
                    <div className="empty">{messages.commonNoOptions}</div>
                  ) : null}

                  {item.subItemsAll ? (
                    <div className="item sub-item">
                      <ItemElement
                        checked={
                          rootItemState?.[
                            `${item.value}.${item.subItemsAll.value}`
                          ]
                        }
                        label={item.subItemsAll.label}
                        onCheckedChange={(checked) =>
                          handleCheckedAllItem(
                            checked,
                            `${item.value}.${item.subItemsAll.value}`,
                            item.value
                          )
                        }
                        withCheckbox={true}
                      />
                    </div>
                  ) : null}

                  {item.subItems.map((subItem, i) => {
                    const value = `${item.value}.${subItem.value}`;

                    return (
                      <div
                        className="item sub-item"
                        key={`${subItem.value}-${i}`}
                      >
                        <ItemElement
                          checked={rootItemState?.[value]}
                          label={subItem.label}
                          onCheckedChange={(checked) =>
                            handleCheckedSubItem(
                              checked,
                              value,
                              item.subItemsAll
                                ? `${item.value}.${item.subItemsAll.value}`
                                : undefined
                            )
                          }
                          withCheckbox={true}
                        />
                      </div>
                    );
                  })}
                </FilterButtonSubMenu>
              </div>
            ) : (
              <div className="item" key={item.value}>
                <ItemElement
                  checked={rootItemState?.[item.value]}
                  label={item.label}
                  labelSubtitle={item.labelSubtitle}
                  onCheckedChange={(checked) =>
                    handleCheckedSubItem(checked, item.value, itemsAll?.value)
                  }
                  withCheckbox={true}
                />
              </div>
            );
          })
        ) : allowMultiple ? (
          visibleItems.map((item) => (
            <div className="item" key={item.value}>
              <ItemElement
                checked={rootItemState?.[item.value]}
                label={item.label}
                labelSubtitle={item.labelSubtitle}
                onCheckedChange={(checked) =>
                  handleCheckedSubItem(checked, item.value, itemsAll?.value)
                }
                withCheckbox={true}
              />
            </div>
          ))
        ) : (
          <FilterButtonRadioGroup
            items={visibleItems.map((item) => ({
              label: item.label,
              value: item.value
            }))}
            onValueChange={handleSelectedRadioSubItem}
            value={Object.keys(rootItemState || {})[0]}
          />
        )}
      </div>

      <style jsx>{`
        .items {
          max-height: 300px;
          overflow: scroll;
        }
        .item:not(:first-child) {
          border-top: 1px solid ${theme.lightGrey};
        }
        .sub-item:first-child {
          padding-top: 1px;
        }
        .empty {
          color: ${theme.textColorLighter};
          font-size: 1.4rem;
          font-weight: 400;
          padding: 25px 10px;
          text-align: center;
        }
      `}</style>
    </div>
  );
};

export default FilterButtonItems;
